import { z } from 'zod'
import { widgetSchema } from '../../common/schema'
import { explorerTranslationsSchema, explorerTranslationDefaults } from './translations'
import { destinationTreeField, destinationPrefixFields, destinationGroupOrderField } from './destinations/schema'
import { nearbyCentroidsField } from './nearby/centroids'
import { stripNullSettingLeaves as coreStripNullSettingLeaves } from '@homerunner-next/widget-core/schema'
// #742: card palette + schema hoisted to common/ so reusing widgets don't import
// from inside the explorer widget. Re-exported below for back-compat.
import { explorerLightColors, explorerDarkColors, explorerColorSchema } from '../../common/card-colors'
// #1163 round 2: the promo-card empty/voiceable checks read a rich body by
// its VISIBLE text, through the same sanitizer the render uses.
import { inlineHtmlVisibleText } from '../../common/sanitize/inline-html'
import { MAX_ICON_SVG_LENGTH } from '../../common/sanitize/icon-svg'
// Single source of truth for the four stay lengths, shared with the flex math
// (#870 slice 6). Pure leaf module — no widget/React imports, so the SSR path
// picks up nothing new.
import { FLEX_STAY_LENGTHS } from './components/filters/flexDates'
import { cardLabelFields, termLabelOverrideSchema, LABEL_OVERRIDES_MAX, cardTagTermSchema, cardTagsSchema, CARD_TAG_TERMS_MAX } from './labels/schema'
import { RATING_MIN_REVIEWS_MAX } from '../../common/property-card/card-rating'
import { tierRatingSchema, tierRowSchema, TIER_ROWS_MAX } from './tier-rating/schema'
// #902: the card-skin leaves and the four lenient wrappers they need now live
// in the shared card module so related-properties (which renders the SAME card
// component) declares the identical group instead of a second copy.
import { cardSkinFields } from '../../common/property-card/card-skin-fields'
import { HEX_COLOR_RE, lenientBoolDefault, lenientEnumDefault, lenientHex, lenientInt, lenientObjectGroup } from '../../common/zod-lenient'

/**
 * PHP/`json_encode` serializes an empty object `{}` as `[]` (PHP can't tell an
 * empty map from an empty list), so a widget saved with no value for an object
 * field round-trips from Central as an empty array and a raw `z.object(...)`
 * throws "Expected object, received array". Wrap the object schema in
 * `phpObject(...)` so an empty `[]` is coerced back to `{}` (letting the inner
 * defaults fill in), while a NON-empty array is still rejected as bad data.
 * Put `.default({})` INSIDE, and chain `.describe()`/`.optional()` on the result.
 */
const phpObject = <T extends z.ZodTypeAny>(shape: T) =>
  z.preprocess((v) => (Array.isArray(v) && v.length === 0 ? {} : v), shape)

// For OPTIONAL fields edited through form inputs: clearing an input yields ''
// (and stored JSON may hold null) — both must mean "unset", not a validation
// error or a coerced 0 (#852).
const emptyToUndef = <T extends z.ZodTypeAny>(schema: T) =>
  z.preprocess((v) => (v === '' || v === null ? undefined : v), schema)


const lenientPattern = (re: RegExp) =>
  z.preprocess((v) => (typeof v === 'string' && re.test(v) ? v : undefined), z.string().optional())

const lenientBool = () =>
  z.preprocess((v) => (typeof v === 'boolean' ? v : undefined), z.boolean().optional())

const lenientEnum = <T extends [string, ...string[]]>(values: T) =>
  z.preprocess((v) => (typeof v === 'string' && (values as readonly string[]).includes(v) ? v : undefined), z.enum(values).optional())


// WP-style string booleans for legacy keys the global-settings sheet writes:
// it stored checkbox state as "1"/"" before the emission bug was fixed (#859),
// and a strict z.boolean() fails the whole parse on those instead of reading
// the admin's intent — every already-saved feed can carry this indefinitely,
// so EVERY checkbox field the Explorer Global Settings sheet renders (every
// leaf in explorerGlobalSchema's pick: the top-level toggles, card, map,
// recommendations) uses this wrapper, not just the fields the issue named.
const wpLenientBoolDefault = (def: boolean) =>
  z.preprocess((v) => (
    typeof v === 'boolean' ? v
      : v === '1' || v === 1 || v === 'true' ? true
        : v === '' || v === '0' || v === 0 || v === 'false' ? false
          : undefined
  ), z.boolean().default(def))

// #1350: legacy numeric STRINGS ("3", "2.5") on the number leaves the
// Explorer Global Settings sheet renders (grid dimensions, price step, card
// counts, map zoom) — the same WP-string class wpLenientBoolDefault fixed for
// checkboxes (#859), one leaf class over. Unlike lenientIntDefault above
// (which silently DEGRADES an out-of-range value to the default), this
// wrapper must never loosen a leaf's own bounds: it only bridges the
// STRING -> NUMBER type gap, then hands the coerced number to the SAME
// z.number()...int()/min()/max() chain the leaf already had (passed in as
// `schema`, not rebuilt here), so a legacy out-of-range string ("99" on a
// max-6 leaf) fails to parse exactly like the number 99 does today —
// coercion never bypasses range validation. A non-numeric string ("" or
// "abc") degrades to the leaf's default, same contract as wpLenientBoolDefault.
const wpLenientNumberDefault = <T extends z.ZodNumber>(schema: T, def: number) =>
  z.preprocess((v) => (
    typeof v === 'number' ? v
      : typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v)) ? Number(v)
        : undefined
  ), schema.default(def))

const lenientIntDefault = (min: number, max: number, def: number) =>
  z.preprocess((v) => {
    const n = typeof v === 'number' && Number.isInteger(v) ? v
      : typeof v === 'string' && /^\d+$/.test(v) ? parseInt(v, 10)
      : undefined
    return n === undefined || n < min || n > max ? undefined : n
  }, z.number().int().min(min).max(max).default(def))

// Clamping variant for bounded percentages (#1145 promo-card overlay opacity):
// an out-of-range integer clamps to the nearest bound instead of degrading to
// the default (150 reads as "fully opaque", which is what the operator meant),
// while a non-integer still degrades to the default.
const lenientIntClampDefault = (min: number, max: number, def: number) =>
  z.preprocess((v) => {
    const n = typeof v === 'number' && Number.isInteger(v) ? v
      : typeof v === 'string' && /^\d+$/.test(v) ? parseInt(v, 10)
      : undefined
    return n === undefined ? undefined : Math.min(max, Math.max(min, n))
  }, z.number().int().min(min).max(max).default(def))


const lenientStringDefault = (def: string) =>
  z.preprocess((v) => (typeof v === 'string' ? v : undefined), z.string().default(def))

// Like lenientStringDefault, but a blank value also falls back to the default
// (toolkit #816: "blank uses Destination / Dates / Guests"), and the stored
// text is trimmed.
const lenientTrimmedStringDefault = (def: string) =>
  z.preprocess((v) => (typeof v === 'string' && v.trim() !== '' ? v.trim() : undefined), z.string().default(def))

// A short editable list of lines (toolkit `text_lines` settings). Non-string
// and blank rows are dropped rather than rejected, and an empty result falls
// back to the built-in examples — an admin who clears every row still gets a
// usable pane instead of an empty one.
const lenientStringLines = (max: number, def: string[]) =>
  z.preprocess((v) => {
    if (!Array.isArray(v)) return undefined
    const rows = v.filter((r): r is string => typeof r === 'string' && r.trim() !== '').slice(0, max)
    return rows.length > 0 ? rows : undefined
  }, z.array(z.string()).max(max).default(def))

const lenientHexDefault = (def: string) =>
  z.preprocess((v) => (typeof v === 'string' && HEX_COLOR_RE.test(v.trim()) ? v.trim() : undefined), z.string().default(def))


/**
 * Quick-filter chips (#853). Each chip submits a RESOLVED Central term ID
 * under a canonical visitor-filter key — never WP slugs or term IDs (visitor
 * filters expect CSV Central IDs). filterKey doubles as the visitor-filter
 * param the chip writes.
 */
export const chipFilterKeys = ['amenities', 'groups', 'tags', 'types', 'cities', 'locations'] as const

/** Iconify icon id, `prefix:name`, each segment lowercase alnum/hyphen. Same shape the dashboard picker validates. */
export const CHIP_ICON_NAME_RE = /^[a-z0-9-]+:[a-z0-9-]+$/i

// Lenient optional string leaf for the chip icon fields: a malformed or
// oversized stored value degrades to "no icon" for THAT chip rather than
// failing the row schema and dropping the whole chip (lenientChipItems).
const lenientOptionalString = (max: number, pattern?: RegExp) =>
  z.preprocess(
    (v) => (typeof v === 'string' && v !== '' && v.length <= max && (!pattern || pattern.test(v)) ? v : undefined),
    z.string().optional(),
  )

export const searchbarChipSchema = z.object({
  label: z.string().min(1).max(80).describe('Chip label text'),
  filterKey: z.enum(chipFilterKeys).describe('Visitor-filter dimension the chip submits under'),
  centralId: z.coerce.number().int().positive().describe('Resolved Central term ID the chip applies'),
  // #871 / PR #1415 direction change (Eli 2026-09-07): chip icons render from
  // inline SVG stored in the config, never from a remote URL, so the customer
  // page makes no third-party request (same call as #1423's amenity icons).
  iconSvg: lenientOptionalString(MAX_ICON_SVG_LENGTH)
    .describe('Inline SVG markup for the chip icon (max 16 KB). The dashboard picker fetches it from Iconify ONCE at selection time and stores it after common/sanitize/icon-svg; the widget sanitizes it again at render and renders it inline. Empty = icon-less chip (toolkit accepts icon-less chips)'),
  iconName: lenientOptionalString(120, CHIP_ICON_NAME_RE)
    .describe('Iconify id (`prefix:name`) the iconSvg came from, kept for display and re-selection in the dashboard picker. Not read by the widget'),
  iconUrl: emptyToUndef(z.string().max(500).regex(/^https:\/\//).optional())
    .describe('LEGACY, never rendered: remote icon image URL from the pre-#871 manual field, the interim part-1 Iconify picker, or the toolkit import. Still accepted so stored blobs keep parsing, but the render path ignores it (no <img> to a remote host, no fetch). Re-select the icon in the dashboard to populate iconSvg'),
})
export type SearchbarChip = z.infer<typeof searchbarChipSchema>

// Render-time safety for admin-entered rows: drop rows the row schema rejects
// (and anything past the 20-chip cap) instead of letting one malformed chip
// brick the widget at parse time.
const lenientChipItems = z.preprocess(
  (v) => (Array.isArray(v) ? v.filter((r) => searchbarChipSchema.safeParse(r).success).slice(0, 20) : undefined),
  z.array(searchbarChipSchema).max(20).default([]),
)

/**
 * Flexible-dates stay lengths (#870 slice 6). Mirrors the toolkit's
 * `Settings::normalize_stay_lengths` (includes/FlexibleSearch/Settings.php:
 * 64-77) exactly: keep valid keys in the order given, drop duplicates and
 * anything unrecognized, and fall back to ALL FOUR when nothing survives —
 * an empty chip row would strand the flexible panel with no way to commit.
 */
const lenientStayLengths = z.preprocess(
  (v) => {
    if (!Array.isArray(v)) return undefined
    const out: string[] = []
    for (const candidate of v) {
      if (typeof candidate !== 'string') continue
      if (!(FLEX_STAY_LENGTHS as readonly string[]).includes(candidate)) continue
      if (out.includes(candidate)) continue
      out.push(candidate)
    }
    return out.length ? out : undefined
  },
  z.array(z.enum(FLEX_STAY_LENGTHS)).min(1).default([...FLEX_STAY_LENGTHS]),
)

export const sortOptionKeys = [
  'featured', 'created_desc', 'created_asc', 'title_asc', 'title_desc',
  'base_price_asc', 'base_price_desc', 'lowest_price_asc', 'lowest_price_desc',
  'average_price_asc', 'average_price_desc', 'bedrooms_asc', 'bedrooms_desc',
  'guests_asc', 'guests_desc', 'rating_desc', 'rating_asc', 'random',
] as const

export type SortOptionKey = (typeof sortOptionKeys)[number]

/**
 * The shape `resolveSortOptions` / `resolveDefaultSort` (./sort) read.
 * Structural, not `z.infer`, so the SSR prefetch and the dropdown can pass
 * their already-parsed `options.sort` and tests can pass a literal.
 *
 * The resolvers live in ./sort rather than here on purpose: the dead-setting
 * sentinel excludes config.ts from usage analysis (schema declaration, never a
 * usage site), so a leaf read only from this file is reported dead — which is
 * exactly what happened to `sort.options`.
 */
export type SortSettings = { default: SortOptionKey; options?: readonly SortOptionKey[] }

/**
 * THE collation for destination names — the runtime Where field and the
 * toolkit importer's baked curation CSVs must share one comparator, or their
 * accent/case tie-breaks diverge and "alpha re-sorts to the same order"
 * silently stops being true. Locale pinned to 'en' + base sensitivity: the
 * same order for every visitor runtime, accents beside their base letter
 * (a deliberate, documented improvement over the module's byte-order
 * strcasecmp, which files 'Ávila' after 'Z').
 *
 * Names are TRIMMED before collating. The two sides of the shared comparator
 * see the same term under two spellings: a WP export carries the name exactly
 * as the admin typed it, while Central's copy has been through WP's
 * `sanitize_text_field` (hooked on `pre_term_name`), which trims. Collating the
 * raw strings files ' Zoo' before 'Aspen' on the importer side (a space
 * collates below every letter) and after it at render time, so the order baked
 * into the curation CSV silently disagrees with the order the site shows.
 * Trimming here matches the key `resolveTerms` already uses for name matching.
 * Case is deliberately NOT folded beyond `sensitivity: 'base'` (which already
 * makes the comparison case-insensitive) — Central treats case-distinct names
 * as distinct terms, and ties keep input order via the stable sort.
 */
export function compareDestinationNames(a: string, b: string): number {
  return a.trim().localeCompare(b.trim(), 'en', { sensitivity: 'base' })
}

export const explorerFilterSchema = z.object({
  platform: z.string().optional().describe('Platform UUID — optional filter to scope by platform'),
  limit: z.number().int().min(1).max(100).default(12).describe('Number of properties per page'),
})

export const visitorFilterSchema = z.object({
  checkin: z.string().optional().describe('Check-in date (YYYY-MM-DD)'),
  checkout: z.string().optional().describe('Check-out date (YYYY-MM-DD)'),
  adults: z.number().int().optional().describe('Number of adults'),
  children: z.number().int().optional().describe('Number of children'),
  pets: z.coerce.boolean().optional().describe('Pet-friendly filter'),
  types: z.string().optional().describe('Comma-separated property type IDs'),
  amenities: z.string().optional().describe('Comma-separated amenity IDs (match any)'),
  amenities_and: z.string().optional().describe('Comma-separated amenity IDs (must have all)'),
  locations: z.string().optional().describe('Comma-separated location IDs'),
  cities: z.string().optional().describe('Comma-separated city IDs'),
  groups: z.string().optional().describe('Comma-separated group IDs'),
  state: z.string().optional().describe('Comma-separated state values (free-text, #511)'),
  country: z.string().optional().describe('Comma-separated country values (free-text, #511)'),
  featured: z.coerce.boolean().optional().describe('Show featured properties only'),
  keywords: z.string().optional().describe('Search by property name'),
  sort_by: z.string().optional().describe('Sort field'),
  tags: z.string().optional().describe('Comma-separated tag IDs'),
  guests: z.number().int().optional().describe('Total guest count'),
  bedrooms: z.number().int().optional().describe('Minimum number of bedrooms'),
  bedrooms_eq: z.number().int().optional().describe('Exact number of bedrooms (overrides bedrooms)'),
  bathrooms: z.number().int().optional().describe('Minimum number of bathrooms'),
  price_min: z.number().optional().describe('Minimum price'),
  price_max: z.number().optional().describe('Maximum price'),
  price_field: z.enum(['base', 'lowest', 'average']).optional().describe('Price column the filter/sort/bounds use, derived from the price-display mode (#441)'),
  map_bounds: z.string().optional().describe('Map bounds string: west,south,east,north'),
  exclude_property: z.string().optional().describe('Property slug to exclude from results'),
  exclude: z.string().optional().describe('Comma-separated Central property IDs to exclude'),
  ctid: z.string().optional().describe('Comma-separated Central property IDs to restrict results to'),
})

export type VisitorFilter = z.infer<typeof visitorFilterSchema>

// Palette + schema now live in common/card-colors (#742). Re-exported here so
// existing `from '../explorer/config'` importers keep working.
export { explorerLightColors, explorerDarkColors, explorerColorSchema }

// ── Marketing (promo) cards (#1145) ──────────────────────────────────────────
// Port of ckit's Marketing Cards tool (MarketingCardsTool/Settings/Renderer),
// folded into explorer: one operator-authored card after every Nth listing.
// Caps mirror MarketingCardsSettings: MIN_FREQUENCY 2, MAX_CARDS 20, title 120,
// eyebrow/CTA 60, body 2000, URL 600. v1 deliberately narrows WP twice, per the
// epic plan's lane note: plain-text body (WP allows a narrow kses allowlist)
// and https/relative links only (WP also allows mailto:/tel:). The v2 slice
// (#1163) re-admits mailto:/tel: for the card LINK under the scheme-specific
// rules below (image/asset URLs stay https/relative), and re-admits a minimal
// rich-text BODY — bold/em/line breaks through common/sanitize/inline-html,
// sanitized at render on both paths — behind the per-card bodyRichText
// opt-in. The plain-text default stays.
export const MARKETING_CARDS_MAX = 20
export const MARKETING_CARD_LIMITS = { title: 120, short: 60, body: 2000, url: 600 } as const

/**
 * Link/asset URL policy ("https/relative + noopener"): an absolute https://
 * URL WITH a host, or a site-relative path starting with a single `/`.
 * Protocol-relative (`//host` — and `/\host`, which browsers normalize to the
 * same thing), `http:`, `javascript:`, `data:`, `mailto:` and `tel:` are all
 * rejected, as is a bare `https://` with no host (an unnavigable CTA). Empty
 * means "no link".
 */
export const isSafeCardHref = (v: string): boolean => {
  if (v === '') return true
  // Browsers strip TAB/CR/LF while parsing URLs, so "/\t/evil.example"
  // normalizes to "//evil.example" (protocol-relative). No legitimate https
  // or /relative URL contains a control or whitespace character — reject them
  // all up front rather than enumerating the strippable ones.
  if (/[\u0000-\u001f\u007f\s]/.test(v)) return false
  if (v.startsWith('/')) return !/^\/[/\\]/.test(v)
  // Both checks are load-bearing: the prefix regex rejects "https:///x"
  // (which the WHATWG parser would normalize to host "x"), while new URL()
  // rejects hostless shells like "https://:443/x" that satisfy the regex but
  // do not parse. Parse failure means unsafe — false is the verdict here,
  // not a masked error.
  if (!/^https:\/\/[^\s/?#\\]+/i.test(v)) return false
  try {
    const u = new URL(v)
    return u.protocol === 'https:' && u.hostname !== ''
  } catch {
    return false
  }
}

/**
 * A card asset URL that can ALSO be interpolated inside `url("…")` in an
 * inline style (the background image). The character class IS the allowlist:
 * anything that could terminate the `url(` token or the declaration —
 * quotes, parens, backslash, whitespace, `;{}<>` — is absent from it, so a
 * hostile value yields no background image rather than a breakout. It lives
 * here, next to the href policy, because the render-time emitter
 * (`safeCssUrl` in marketingCards.ts) and the dashboard advisory
 * (`marketingCardNeedsAccessibleLabel`) must agree on which background
 * images actually render.
 */
export const isCssSafeCardUrl = (v: string): boolean =>
  v !== '' &&
  v.length <= MARKETING_CARD_LIMITS.url &&
  isSafeCardHref(v) &&
  /^[A-Za-z0-9\-._~:/?#[\]@!$&+,=%]+$/.test(v)

/**
 * `mailto:` link policy (#1163 v2 slice): exactly one plain address and
 * nothing else. Local part and domain are an ASCII allowlist (letters,
 * digits, `._+-`; dot-joined domain labels, at least one dot). Header fields
 * (`?subject=`, `?cc=`, `?body=`) are out of scope for v1 and are the
 * injection surface — a CR/LF smuggled through `%0d%0a` becomes a header —
 * so `?`, `#`, and `%` are rejected outright, as are `,` and `;` (one
 * address). Both checks are load-bearing exactly as for https: the anchored
 * regex is the shape allowlist, and `new URL()` is the browser's own reading
 * of the value, which must agree that the scheme is mailto with NO query or
 * fragment component and the path is the address we validated — so a future
 * loosening of the regex cannot silently admit headers.
 */
const MAILTO_RE = /^mailto:[a-z0-9._+-]+@[a-z0-9-]+(?:\.[a-z0-9-]+)+$/i
export const isSafeMailtoHref = (v: string): boolean => {
  if (/[\u0000-\u001f\u007f\s]/.test(v)) return false
  if (!MAILTO_RE.test(v)) return false
  try {
    const u = new URL(v)
    return u.protocol === 'mailto:' && u.search === '' && u.hash === '' && u.pathname === v.slice('mailto:'.length)
  } catch {
    return false
  }
}

/**
 * `tel:` link policy (#1163 v2 slice): digits, the RFC 3966 visual
 * separators (`-`, `.`, `(`, `)`), `+` for a global number, and plain
 * spaces — with at least one digit, since a separator-only value is
 * unnavigable (the tel twin of the hostless https shell). Only U+0020 is
 * admitted; every other whitespace and control character is rejected up
 * front as for https (browsers strip TAB/CR/LF while parsing). No letters,
 * no `;` (RFC 3966 parameters), no `?`, `#`, `%`. `new URL()` again confirms
 * the browser reads a tel scheme with no query or fragment.
 */
const TEL_RE = /^tel:[0-9+\-.() ]+$/i
export const isSafeTelHref = (v: string): boolean => {
  if (/[\u0000-\u001f\u007f]/.test(v) || /[^\S ]/.test(v)) return false
  if (!TEL_RE.test(v) || !/[0-9]/.test(v)) return false
  try {
    const u = new URL(v)
    return u.protocol === 'tel:' && u.search === '' && u.hash === ''
  } catch {
    return false
  }
}

/** The card LINK policy: the asset policy plus mailto:/tel:. Empty means "no link". */
export const isSafeCardLinkHref = (v: string): boolean =>
  isSafeCardHref(v) || isSafeMailtoHref(v) || isSafeTelHref(v)

// Form inputs clear to '' and Central nullifies empties; both read as "unset".
const cardText = (max: number) => emptyToUndef(z.string().trim().max(max).optional())
const cardHref = (max: number) =>
  emptyToUndef(
    z.string().trim().max(max)
      .refine(isSafeCardHref, { message: 'Must be an https:// URL or a relative path starting with /' })
      .optional(),
  )
const cardLinkHref = (max: number) =>
  emptyToUndef(
    z.string().trim().max(max)
      .refine(isSafeCardLinkHref, {
        message: 'Must be an https:// URL, a relative path starting with /, a mailto: address, or a tel: number',
      })
      .optional(),
  )

export const marketingCardSchema = z.object({
  id: cardText(64).describe('Optional stable id reported in impression/click events; blank = position-based'),
  // Scalar leaves are lenient (#1145 review): a bad stored boolean/opacity
  // degrades that FIELD to its default/clamp, never the whole card. Links and
  // text caps stay row-fatal (a bad link must never render) — the dashboard
  // surfaces those through marketingCardDiagnostics below.
  enabled: lenientBoolDefault(true).describe('Include this card in the rotation'),
  eyebrow: cardText(MARKETING_CARD_LIMITS.short).describe('Small kicker text above the title (max 60)'),
  title: cardText(MARKETING_CARD_LIMITS.title).describe('Card title (max 120)'),
  body: cardText(MARKETING_CARD_LIMITS.body).describe('Card text, line breaks kept (max 2000). Plain text unless bodyRichText opts into the minimal inline allowlist (#1163)'),
  // Lenient like the other scalar leaves — with a security edge: a malformed
  // stored value must read as OFF (the plain-text path), never as "render
  // this body as HTML".
  bodyRichText: lenientBoolDefault(false).describe('Render the text through the #1163 inline allowlist (bold/em/line breaks via strong/b/em/i/br, sanitized at render; everything else is removed). Off = plain text exactly as typed'),
  accessibleLabel: cardText(MARKETING_CARD_LIMITS.title).describe('Screen-reader name for the card region and its whole-card link (max 120). Needed for image-only cards; blank = eyebrow for the region, title/eyebrow/text for the link'),
  imageUrl: cardHref(MARKETING_CARD_LIMITS.url).describe('Inline image above the text (https:// or /relative)'),
  bgImageUrl: cardHref(MARKETING_CARD_LIMITS.url).describe('Cover background image behind the text (https:// or /relative)'),
  bgOverlayColor: lenientHexDefault('#000000').describe('Tint layered over the background image so copy stays legible'),
  bgOverlayOpacity: lenientIntClampDefault(0, 100, 40).describe('Overlay opacity, 0-100 (cleared = 40; out of range clamps)'),
  linkUrl: cardLinkHref(MARKETING_CARD_LIMITS.url).describe('Destination (https://, /relative, mailto:address, or tel:number). Empty = no link'),
  ctaText: cardText(MARKETING_CARD_LIMITS.short).describe('Button label (max 60). Needs a link to render'),
  newTab: lenientBoolDefault(true).describe('Open the link in a new tab (rel="noopener" is always set)'),
  linkWholeCard: lenientBoolDefault(false).describe('Make the whole card clickable, not just the button'),
  useGlobalStyle: lenientBoolDefault(true).describe('Inherit the global card colors; off = use the overrides below'),
  bgColor: lenientHex().describe('Background override (hex)'),
  textColor: lenientHex().describe('Text override (hex)'),
  buttonBg: lenientHex().describe('Button background override (hex)'),
  buttonText: lenientHex().describe('Button text override (hex)'),
})
export type MarketingCard = z.infer<typeof marketingCardSchema>

/**
 * Operator-facing STRICT view of one card row (#1145 review, finding 5). The
 * render schema above is deliberately lenient (a bad scalar degrades, a bad
 * link drops the row) so the grid never breaks — which also means none of
 * those rules can surface as a form field error. The dashboard runs each
 * authored row through this strict variant and shows the result as a
 * warn-only advisory naming the card and the rule. Cleared inputs ('' / null)
 * still read as "unset" here, exactly as the form produces them.
 */
const strictHex = () =>
  emptyToUndef(z.string().trim().regex(HEX_COLOR_RE, { message: 'Must be a hex color like #0A386B' }).optional())
const marketingCardStrictSchema = marketingCardSchema.extend({
  enabled: emptyToUndef(z.boolean().optional()),
  bodyRichText: emptyToUndef(z.boolean().optional()),
  newTab: emptyToUndef(z.boolean().optional()),
  linkWholeCard: emptyToUndef(z.boolean().optional()),
  useGlobalStyle: emptyToUndef(z.boolean().optional()),
  bgOverlayOpacity: emptyToUndef(z.number().int().min(0).max(100).optional()),
  bgOverlayColor: strictHex(),
  bgColor: strictHex(),
  textColor: strictHex(),
  buttonBg: strictHex(),
  buttonText: strictHex(),
})

export interface MarketingCardDiagnostic {
  /** Card field the rule applies to; '' when the row itself is not an object. */
  field: string
  message: string
  /**
   * True when the lenient render schema drops the whole row (the card never
   * renders on the site); false when the card still renders and only this
   * field falls back to its default or clamp.
   */
  rowDropping: boolean
}

export function marketingCardDiagnostics(row: unknown): MarketingCardDiagnostic[] {
  const result = marketingCardStrictSchema.safeParse(row)
  if (result.success) return []
  const rowDropping = !marketingCardSchema.safeParse(row).success
  return result.error.issues.map((issue) => ({
    field: typeof issue.path[0] === 'string' ? issue.path[0] : '',
    message: issue.message,
    rowDropping,
  }))
}

/**
 * #1162 operator signal: an image-only card (inline or background image, no
 * eyebrow/title/body) has no text a screen reader can voice — its message is
 * in the pixels. Without an authored `accessibleLabel` the card region and
 * its whole-card link fall back to the generic "Promotion". Row-level and
 * pure, for the dashboard's warn-only advisory. A row the render schema
 * rejects is `marketingCardDiagnostics`' job, and a disabled card is not on
 * the site, so neither is reported here.
 */
export function marketingCardNeedsAccessibleLabel(row: unknown): boolean {
  const parsed = marketingCardSchema.safeParse(row)
  if (!parsed.success) return false
  const c = parsed.data
  if (!c.enabled) return false
  // A rich body only counts as voiceable text by its VISIBLE projection
  // (#1163 round 2): a body that sanitizes to nothing must not suppress the
  // nudge for what is, on the site, an image-only card.
  const bodyText = c.bodyRichText === true ? inlineHtmlVisibleText(c.body) : (c.body ?? '')
  const hasText = Boolean(c.eyebrow || c.title || bodyText)
  // A background image counts only if it can actually be emitted as CSS —
  // otherwise the card never takes a slot (activeMarketingCards), and a
  // nudge for a card that is not on the site would be a false alarm.
  const hasImage = Boolean(c.imageUrl) || isCssSafeCardUrl(c.bgImageUrl ?? '')
  return hasImage && !hasText && !c.accessibleLabel
}

// Render-time safety (chips precedent, #853): drop rows the row schema rejects
// and anything past MAX_CARDS instead of letting one malformed stored card
// brick the widget at parse time — the grid must never break because of one
// bad card. A rejected row renders nothing; it is never "repaired".
const lenientMarketingCardItems = z.preprocess(
  (v) => {
    if (!Array.isArray(v)) return undefined
    // Position-based ids resolve from the AUTHORED index before any row is
    // dropped: a valid card that follows an invalid one must keep its id when
    // the bad row is later fixed or removed, or analytics merge two different
    // cards under one id. A whitespace-only id trims to empty later, so it
    // counts as blank here too. The cap applies to AUTHORED rows, before
    // validity filtering — the dashboard advisory states exactly that rule.
    return v
      .map((r, authoredIndex) => {
        if (r === null || typeof r !== 'object' || Array.isArray(r)) return r
        const row = r as Record<string, unknown>
        const blankId = row.id == null || (typeof row.id === 'string' && row.id.trim() === '')
        return blankId ? { ...row, id: `card-${authoredIndex + 1}` } : r
      })
      .slice(0, MARKETING_CARDS_MAX)
      .filter((r) => marketingCardSchema.safeParse(r).success)
  },
  z.array(marketingCardSchema).max(MARKETING_CARDS_MAX).default([]),
)

export const MARKETING_CARDS_GLOBAL_STYLE_DEFAULTS = {
  bgColor: '#0A386B',
  textColor: '#ffffff',
  buttonBg: '#ffffff',
  buttonText: '#0A386B',
} as const

export const marketingCardsGlobalStyleSchema = z.object({
  bgColor: lenientHexDefault(MARKETING_CARDS_GLOBAL_STYLE_DEFAULTS.bgColor).describe('Card background'),
  textColor: lenientHexDefault(MARKETING_CARDS_GLOBAL_STYLE_DEFAULTS.textColor).describe('Card text'),
  buttonBg: lenientHexDefault(MARKETING_CARDS_GLOBAL_STYLE_DEFAULTS.buttonBg).describe('Button background'),
  buttonText: lenientHexDefault(MARKETING_CARDS_GLOBAL_STYLE_DEFAULTS.buttonText).describe('Button text'),
})
export type MarketingCardsGlobalStyle = z.infer<typeof marketingCardsGlobalStyleSchema>

// The group literal carries every leaf default on purpose (recommendations
// precedent): formDefaultValueFromSchema's ZodDefault branch returns this
// literal verbatim without recursing, so a `.default({})` seeds the editor
// with blanks — #ffffff swatches and an empty Frequency — while the live
// card resolves #0A386B and 6. Keep in lock-step with the leaf defaults.
export const MARKETING_CARDS_DEFAULTS = {
  enabled: false,
  frequency: 6,
  cards: [] as MarketingCard[],
  globalStyle: { ...MARKETING_CARDS_GLOBAL_STYLE_DEFAULTS },
}

/**
 * Compare Homes (#1713, ckit `compare_homes`). Every leaf defaults to the
 * state before the feature existed (`enabled: false`), so an explorer that
 * never sets the group renders byte-identically. `max` and `buttonText` are
 * the ckit tool's own limits (2..4 homes, a 30-character label).
 */
export const COMPARE_DEFAULTS = { enabled: false, max: 3, buttonText: 'Compare' } as const
export const COMPARE_MAX_RANGE = { min: 2, max: 4 } as const
export const COMPARE_BUTTON_TEXT_MAX = 30

/**
 * Destination Filter port (#1072 D1) — the stored tree's node contract.
 *
 * Stored FLAT, as node rows, on purpose. The admin form engine materializes a
 * blank value into EVERY widget save, and `getFinalSettings`' leaf merge lets
 * a blank widget value beat a real feed-global one (the #1064 W2 feeLines
 * bug). Per form-default-value-from-schema.ts, an optional NON-ENUM array
 * materializes as `undefined` (L57-58) while an optional OBJECT unwraps to
 * `{}` (L60-63 -> L92-104) — the clobbering shape. A flat optional array is
 * therefore the only tree shape that cannot reproduce that bug.
 *
 * Numbers are coerced because Central round-trips settings through PHP/JSON,
 * where term ids come back as strings often enough to matter at render time.
 */
// The storage contract moved to destinations/schema.ts (#1072 D2) so the
// standalone destination-search widget shares the EXACT same node schema,
// caps, and prefix fields; re-exported here so every existing import keeps
// working.
export { destinationNodeLevels, destinationNodeSchema, MAX_DESTINATION_TREE_ROWS } from './destinations/schema'

/**
 * The render-boundary form of `card.cardTags` (#1812), the one the widget
 * configs declare — this one AND related-properties' (#1819), the SAME object,
 * so the two card surfaces cannot drift (the #902 skin-field rule). Same
 * render-time safety as `labelOverrides` below: slice the ordered term list
 * FIRST so a huge authored array cannot buy unbounded validation work, then
 * drop the rows the row schema rejects, so one malformed row cannot brick the
 * card. The group itself stays the lenient labels/schema.ts schema. Lives
 * here, not in labels/schema.ts: config.ts is the lint-sanctioned home for
 * per-row safeParse leniency (eslint.config.mjs, #1174).
 */
export const cardTagsSettingSchema = z.preprocess(
  (v) => {
    if (typeof v !== 'object' || v === null || Array.isArray(v)) return undefined
    const group = v as Record<string, unknown>
    const terms = Array.isArray(group.terms)
      ? group.terms.slice(0, CARD_TAG_TERMS_MAX).filter((r) => cardTagTermSchema.safeParse(r).success)
      : undefined
    return { ...group, terms }
  },
  cardTagsSchema,
).describe(cardLabelFields.cardTags.description ?? 'Card tags')

/** Card image aspect ratios (`card.imageAspectRatio`, toolkit flag card.image_aspect). */
export const CARD_IMAGE_ASPECT_RATIOS = ['16/9', '4/3', '3/2', '3/4', '1/1'] as const

// Load More button (#872 close-out). Grid-level, not card-markup-level:
// .hfse-load-more-btn renders under every card.design, so unlike the rest of
// the skin group these leaves are live regardless of the design choice.
// Hoisted (#1432) so the custom-preset value schema below reuses the SAME leaf
// definitions the card group spreads — one source for both.
const explorerLoadMoreFields = {
  loadMoreBg: lenientHex().describe('Load More button background; empty = the current bordered button. Skin: hrtk-skin-color-card-load-more-bg'),
  loadMoreText: lenientHex().describe('Load More button text color; empty = the widget foreground. Skin: hrtk-skin-color-card-load-more-text'),
  loadMoreHover: lenientHex().describe('Load More button hover background; empty = darkens the resting background on hover. Skin: hrtk-skin-color-card-load-more-hover'),
  loadMoreRadius: lenientInt(0, 50).describe('Load More button corner radius in px; empty = 4px. Skin: hrtk-skin-radius-card-load-more'),
  loadMoreAlign: lenientEnumDefault(['center', 'left', 'right'], 'center')
    .describe('Load More button alignment. Skin flag: card.load_more_align'),
}

/**
 * Custom card-skin presets (#1432) — the platform port of the toolkit Skin's
 * "Save as preset" (Preset.php CUSTOM_PRESETS_OPTION: {slug: {name, tokens,
 * flags}}). Each entry is a NAMED SNAPSHOT of the Card Template knobs: the
 * dashboard captures the non-neutral `card.*` skin leaves at save time and
 * re-applies them as the same sparse form fill the built-in presets use.
 *
 * - Stored on the explorer instance's own settings (`cardSkinPresets`), so it
 *   rides the existing widget save path: no new endpoint, no feed write. It is
 *   deliberately NOT in explorerGlobalSchema's pick — a preset list is not a
 *   render default to inherit, and the creation-time seeding path must not
 *   copy it into every new widget.
 * - The runtime never reads it: a preset is values at save time, not a lookup
 *   at render time, so SSR and CSR render identically with or without it.
 * - `values` parse through the SAME leaf schemas the card group uses (shared
 *   cardSkinFields + the Load More leaves + imageAspectRatio), unknown keys
 *   stripped. Rows that fail the id/name contract are DROPPED by the
 *   preprocess rather than failing the widget parse: a corrupt preset must
 *   never take the explorer down.
 * - Ids mirror toolkit sanitize_key (lowercase slug); built-in ids
 *   (classic, editorial-square) are reserved on the dashboard side.
 */
export const CARD_SKIN_PRESET_ID_RE = /^[a-z0-9][a-z0-9_-]{0,63}$/
export const CARD_SKIN_PRESET_NAME_MAX = 60

export const cardSkinPresetValuesSchema = z.object({
  ...cardSkinFields,
  ...explorerLoadMoreFields,
  imageAspectRatio: z.enum(CARD_IMAGE_ASPECT_RATIOS),
}).partial()

export const cardSkinPresetSchema = z.object({
  id: z.string().regex(CARD_SKIN_PRESET_ID_RE).describe('Preset id: lowercase slug of the name (toolkit sanitize_key shape)'),
  name: z.string().trim().min(1).max(CARD_SKIN_PRESET_NAME_MAX).describe('Operator-facing preset name'),
  values: cardSkinPresetValuesSchema.describe('The non-neutral Card Template knobs captured at save time (sparse)'),
})

export type ExplorerCardSkinPreset = z.infer<typeof cardSkinPresetSchema>

const cardSkinPresetsSchema = z.preprocess((v) => {
  if (!Array.isArray(v)) return undefined
  const seen = new Set<string>()
  const rows: Array<{ id: string; name: string; values: Record<string, unknown> }> = []
  for (const row of v) {
    if (!row || typeof row !== 'object' || Array.isArray(row)) continue
    const r = row as Record<string, unknown>
    const id = typeof r.id === 'string' ? r.id : ''
    const name = typeof r.name === 'string' ? r.name.trim().slice(0, CARD_SKIN_PRESET_NAME_MAX) : ''
    if (!CARD_SKIN_PRESET_ID_RE.test(id) || name === '' || seen.has(id)) continue
    seen.add(id)
    const values = r.values && typeof r.values === 'object' && !Array.isArray(r.values) ? (r.values as Record<string, unknown>) : {}
    rows.push({ id, name, values })
  }
  return rows
}, z.array(cardSkinPresetSchema).optional())

export const exploreWidgetConfig = widgetSchema.extend({
  section: z.object({
    show: z.boolean().default(false).describe('Show the section title above the widget content'),
    title: z.string().nullish().default('Explore Properties').describe('Section title text'),
  }).default({ show: false, title: 'Explore Properties' }).describe('Section heading shown above the widget content'),
  template: z.enum(['grid', 'slider']).default('grid').describe('Layout template'),
  grid: z.object({
    // #1350: every leaf below is a Explorer Global Settings sheet render
    // surface (explorerGlobalSchema picks `grid` whole), so each needs the
    // same legacy-numeric-string tolerance the sibling booleans got in #1346.
    columns: wpLenientNumberDefault(z.number().int().min(1).max(6), 1).describe('Columns at mobile (< 600px)'),
    columns780: wpLenientNumberDefault(z.number().int().min(1).max(6), 2).describe('Columns at tablet (600-959px)'),
    columns960: wpLenientNumberDefault(z.number().int().min(1).max(6), 3).describe('Columns at desktop (960px+)'),
    // Separate counts while the map is OPEN (#564, WP: grid columns "With
    // Map") — the grid shares the row with the map at 960px+, so it usually
    // wants fewer columns than the map-hidden state.
    columnsWithMap: wpLenientNumberDefault(z.number().int().min(1).max(6), 1).describe('Columns at mobile (< 600px) while the map is open (WP: with-map columns)'),
    columnsWithMap780: wpLenientNumberDefault(z.number().int().min(1).max(6), 2).describe('Columns at tablet (600-959px) while the map is open (WP: with-map columns)'),
    columnsWithMap960: wpLenientNumberDefault(z.number().int().min(1).max(6), 2).describe('Columns at desktop (960px+) while the map is open (WP: with-map columns)'),
    gap: wpLenientNumberDefault(z.number(), 16).describe('Gap between cards in pixels'),
  }).default({ columns: 1, columns780: 2, columns960: 3, columnsWithMap: 1, columnsWithMap780: 2, columnsWithMap960: 2, gap: 16 }).describe('Grid layout settings'),
  slider: z.object({
    hideArrows: z.boolean().default(false).describe('Hide navigation arrows'),
    hideDots: z.boolean().default(false).describe('Hide dot indicators'),
    autoplay: z.boolean().default(false).describe('Enable autoplay'),
    autoplayInterval: z.number().int().min(1000).max(30000).default(5000).describe('Autoplay interval in milliseconds'),
    loop: z.boolean().default(true).describe('Loop back to start'),
  }).default({ hideArrows: false, hideDots: false, autoplay: false, autoplayInterval: 5000, loop: true }).describe('Slider layout settings'),
  infiniteScroll: wpLenientBoolDefault(true).describe('Enable infinite scroll'),
  showLoadMore: wpLenientBoolDefault(true).describe('Show load more button when infinite scroll is off'),
  showFilter: z.boolean().default(true).describe('Show filter UI'),
  primaryFilters: z.array(z.enum(['where', 'date', 'guests', 'additional-filters']))
    .default(['where', 'date', 'guests', 'additional-filters'])
    .describe('Which primary search-bar inputs show and in what order. Uncheck to hide an input; drag to reorder. WP: primary_filter_fields'),
  whereFieldSource: z.enum(['city', 'location', 'group', 'state', 'country', 'zone'])
    .default('city')
    .describe('Which dimension the "Where" field searches. "zone" is a combined Regions (locations) + Cities picker. WP: primary_field_where_source'),
  zoneMultiselect: z.boolean().default(false).describe('Zone Where mode: allow selecting multiple regions/cities. WP: zone_multiselect'),
  hideZoneSearch: z.boolean().default(false).describe('Zone Where mode: hide the search box inside the picker. WP: hide_zone_search'),
  hideZoneGroups: z.boolean().default(false).describe('Zone Where mode: hide the "Our Regions" / "All Cities" headings. WP: hide_zone_groups'),
  showPetsFilter: z.boolean().default(true).describe('Show the Pets stepper row (0-3) in the Guests popup; the search itself only sends pets yes/no, like the toolkit module. WP: explorer_extra_fields includes "pets" by default'),
  showSortDropdown: z.boolean().default(true).describe('Show the sort dropdown in the secondary filter row. WP: explorer_extra_fields includes "order" by default'),
  showListingsCount: z.boolean().default(true).describe('Show the matching-property count in the results header ("12 homes" on the pro bar, "N Properties" on legacy). Toolkit: enable_explorer_count (v3.214.12 split from enable_cta_count; deliberate default divergence — see the parity ledger)'),
  // date-fns tokens. The dashboard offers a preset list plus a free-text
  // "custom" input (#560); DateFilter falls back to the default at
  // render time if a custom string is invalid. Shared tokens (LLL, MMM, dd,
  // yyyy) render the same as luxon, so WP's imported 'LLL dd' just works.
  dateFormat: z.string().default('MMM d, yyyy')
    .describe('Display format for the selected dates in the Date field trigger, date-fns tokens (#560). WP: explorer_date_format'),
  // Toolkit port (#852): Searchbar Pro lets bedrooms and bathrooms be hidden
  // independently (filter_show_bedrooms / filter_show_bathrooms), while the
  // 'rooms' section in additionalFilters covers both. Optional with no default —
  // absent = shown (current behavior); consumers fall back to true.
  showBedroomsFilter: lenientBool().describe('Show the Bedrooms stepper inside the Filters popup rooms section. Toolkit: filter_show_bedrooms'),
  showBathroomsFilter: lenientBool().describe('Show the Bathrooms stepper inside the Filters popup rooms section. Toolkit: filter_show_bathrooms'),
  // 'keyword' (Filter by Name) is OFF by default as of r7 (#936, Eli): it
  // reads as a search box but narrows results, so it is opt-in. Feeds with an
  // explicitly saved section list (including WP imports of
  // additional_filter_fields) keep whatever they saved.
  additionalFilters: z.array(z.enum(['keyword', 'price', 'rooms', 'types', 'amenities']))
    .default(['rooms', 'types', 'price', 'amenities'])
    .describe('Which sections show in the additional-filters (Filters) popup, in this order: Filter by Name, Price Range, Bedrooms/Bathrooms, Types of Place, Features & Amenities. Check to show a section; drag to reorder. Filter by Name is off by default. WP: additional_filter_fields'),
  primaryAmenitiesCount: z.coerce.number().int().min(1).default(8)
    .describe('How many amenities show in the Filters popup by default; a "More Amenities" button reveals the rest. WP: primary_features_number'),
  amenitiesColumns: z.coerce.number().int().min(1).max(4).default(3)
    .describe('Columns for the Features & Amenities checkbox grid in the Filters popup (mobile stays at 2). WP: primary_features_col_md'),
  filterOptions: phpObject(z.object({
    cities: z.string().default(''),
    locations: z.string().default(''),
    groups: z.string().default(''),
    types: z.string().default(''),
    amenities: z.string().default(''),
    states: z.string().default(''),
    countries: z.string().default(''),
  }).default({})).describe('Curate which filter values are offered and in what order, per dimension (comma-separated ordered IDs). Empty = show all in default order; otherwise only the chosen values, in the chosen order. WP: explorer_terms_*'),
  showSecondaryFilters: z.boolean().default(true).describe('Show secondary filter row (filters button, sort, property count)'),
  showGrid: z.boolean().default(true).describe('Show property grid/slider'),
  showMap: z.boolean().default(false).describe('Show map'),
  openMapOnMount: z.boolean().default(false).describe('Open the map by default when the widget loads. Only takes effect when showMap is true.'),
  fullPrice: wpLenientBoolDefault(false).describe('Calculate full price for date queries'),
  fullPriceAfterTax: wpLenientBoolDefault(false).describe('Show full prices including taxes and fees (WP plugin parity)'),
  showOriginalTotal: wpLenientBoolDefault(false).describe('When a discount applies, show the pre-discount total struck through beside the discounted total on cards with a full-price quote. Rounding-only differences never strike. Off by default, like the toolkit Original Total module.'),
  perPageWithFullPrice: z.number().int().min(0).default(0).describe('Separate grid page size when full price is on (per-property quotes are heavier, so fewer per page; 0 = use the normal per-page). WP: per_page_with_full_price'),
  noItemsText: z.string().optional().describe('Message when no results'),
  submitBtnText: z.string().optional().describe('Custom text for the filter search button'),
  stickyFilter: z.boolean().default(false).describe('Keep the filter bar pinned to the top of the viewport while scrolling. Legacy searchbar: the whole filter block pins (position:sticky). Searchbar Pro: only the bar pins — transparent shell over the scrim, condensing to a compressed pill 64px past the bar and expanding back on click (toolkit initSticky refine branch, searchbar-pro.js:4771-5029; desktop only)'),
  requireAllAmenities: z.boolean().default(false).describe('Require properties to have ALL selected amenities (AND match) instead of any (OR)'),
  priceStep: wpLenientNumberDefault(z.number().int().min(1).max(1000), 5).describe('Step increment for the price range filter slider'),
  recommendations: z.object({
    text: z.string().optional().describe('Heading shown above recommended properties'),
    perPage: wpLenientNumberDefault(z.number().int().min(1).max(24), 4).describe('How many recommendations to show'),
    showFeatured: wpLenientBoolDefault(true).describe('Source recommendations from featured properties'),
    tags: z.string().optional().describe('Comma-separated tag IDs to source recommendations from'),
  }).default({ perPage: 4, showFeatured: true }).describe('Fallback recommendations shown when no results match (requires the Recommendations feature toggle)'),
  // Both scalar leaves are lenient (#1145 review, finding 1): the form
  // autosaves raw, so a stored frequency of 1 (typed, or a pause at '1' on
  // the way to '12') must degrade to the default — a strict leaf would throw
  // at RENDER in both ssr.ts and widget.tsx and take every listing with it.
  marketingCards: phpObject(z.object({
    enabled: lenientBoolDefault(MARKETING_CARDS_DEFAULTS.enabled).describe('Insert promo cards into the results grid (grid template only; never in slider mode or the no-results state)'),
    frequency: lenientIntDefault(2, 50, MARKETING_CARDS_DEFAULTS.frequency).describe('Insert a card after every Nth loaded listing (positions N, 2N, 3N…). Out of range or invalid reads as the default. WP: MIN_FREQUENCY 2, default 6'),
    cards: lenientMarketingCardItems.describe('Promo cards, rotated in authored order through the insertion slots (max 20). Disabled or content-empty cards never take a slot'),
    globalStyle: phpObject(marketingCardsGlobalStyleSchema.default({ ...MARKETING_CARDS_GLOBAL_STYLE_DEFAULTS })).describe('Colors every card inherits unless it turns Use Global Style off'),
  }).default({ ...MARKETING_CARDS_DEFAULTS, globalStyle: { ...MARKETING_CARDS_GLOBAL_STYLE_DEFAULTS } })).describe('Operator-authored promo cards interleaved into the results grid (ckit Marketing Cards parity, #1145)'),
  // Compare Homes (#1713). A NEW group, so `lenientObjectGroup`: a stale or
  // PHP-emptied (`[]`) value degrades to the defaults instead of throwing at
  // render. Every leaf is lenient for the same reason; `.default()` and
  // `.describe()` sit INSIDE the wrapper per its contract.
  compare: lenientObjectGroup(z.object({
    enabled: lenientBoolDefault(COMPARE_DEFAULTS.enabled).describe('Let guests tick two to four homes on the results and compare them side by side: photo, sleeps, bedrooms, bathrooms, price, location and amenities. Off by default, so nothing changes until you turn it on'),
    max: lenientIntDefault(COMPARE_MAX_RANGE.min, COMPARE_MAX_RANGE.max, COMPARE_DEFAULTS.max).describe('The most homes a guest can compare at once (2 to 4). Three reads comfortably on a laptop; four needs a wide screen. Out of range reads as 3'),
    buttonText: z.preprocess(
      (v) => (typeof v === 'string' && v.trim() !== '' ? v.trim().slice(0, COMPARE_BUTTON_TEXT_MAX) : undefined),
      z.string().default(COMPARE_DEFAULTS.buttonText),
    ).describe('The label on the Compare toggle above the results, on each card\'s pick chip while comparing, and on the Compare button. Keep it to one or two words (30 characters at most)'),
    accentColor: lenientHex().describe('Color for the Compare button, the picked state and the check marks. Empty uses the explorer\'s own accent'),
  }).default({ ...COMPARE_DEFAULTS }).describe('Compare Homes (ckit Compare Homes parity, #1713)')),
  card: z.object({
    // Named `design`, not `template`: the admin panel already renders a
    // synthetic "Card Template" sub-panel under the children key `template`
    // (the #872 skin group), which would clobber a real field of that name.
    // Classic is the default (replace-don't-preserve): the shared Property
    // Card templates ARE the explorer card. 'default' keeps the legacy
    // explorer render selectable; since #902's port the skin settings apply
    // to every design (four knobs are Legacy-only — see card-skin-css.ts).
    design: lenientEnumDefault(['default', 'classic', 'overlay', 'detailed'], 'classic')
      .describe('Card design. Classic, Photo Overlay, and Detailed render the shared Property Card templates in the grid; Legacy is the card this widget rendered before templates. The Card Template (skin) settings apply to all designs, except a few Legacy-only knobs (metaLayout, metaSpecStyle, showTypeIcon, subtitleFontSize) and documented Photo Overlay exceptions (priceColor, body paddings, structural orders)'),
    openInNewTab: wpLenientBoolDefault(false).describe('Open property pages in a new tab'),
    showLocation: wpLenientBoolDefault(true).describe('Show the property location in card meta'),
    showType: wpLenientBoolDefault(true).describe('Show the property type in card meta'),
    showFeatures: wpLenientBoolDefault(true).describe('Show guests/bedrooms/bathrooms in card meta'),
    showRating: wpLenientBoolDefault(false).describe('Show the review rating and count when available'),
    showGroups: wpLenientBoolDefault(true).describe('Show the property group/tag label badge on cards (WP: hide_groups inverse)'),
    // Card rating (toolkit `card-rating` parity): threshold + placement. Optional
    // and never materialized; unset = the rating exactly as it rendered before
    // (any review count above 0, in the title row). Resolver: common/property-card/card-rating.ts.
    ratingMinReviews: z.preprocess((v) => {
      const n = typeof v === 'number' && Number.isInteger(v) ? v
        : typeof v === 'string' && /^\d+$/.test(v) ? parseInt(v, 10)
        : undefined
      return n === undefined ? undefined : Math.min(RATING_MIN_REVIEWS_MAX, Math.max(1, n))
    }, z.number().int().min(1).max(RATING_MIN_REVIEWS_MAX).optional())
      .describe('Hide the rating on homes with fewer reviews than this (1 to 100). Empty = show it for any home with reviews (toolkit card-rating: min_reviews)'),
    ratingPlacement: lenientEnum(['title', 'location'])
      .describe('Where the rating sits: in the title row (default) or after the location line (toolkit card-rating: placement). Also applies to the map popup'),
    // Label Designer (#1069): per-term badge styling; absent = the single primary-tag badge above.
    // Render-time safety for admin-entered rows, same class as the chips above:
    // slice FIRST so a huge authored array cannot buy unbounded validation work
    // (the strict schema caps at 50 anyway), then drop rows the row schema
    // rejects instead of letting one malformed row brick the card.
    ...cardLabelFields,
    labelOverrides: z.preprocess(
      (v) => (Array.isArray(v)
        ? v.slice(0, LABEL_OVERRIDES_MAX).filter((r) => termLabelOverrideSchema.safeParse(r).success)
        : undefined),
      z.array(termLabelOverrideSchema).max(LABEL_OVERRIDES_MAX).optional(),
    ).describe(cardLabelFields.labelOverrides.description ?? 'Terms that get a badge on the card'),
    // Tier Rating (#1813, toolkit `tier-rating`): an ordered ladder of terms →
    // one tier badge per card. Optional and never materialized. Same render
    // safety as labelOverrides: slice first, then drop rows the strict row
    // schema rejects instead of letting one malformed row brick the card.
    tierRating: z.preprocess(
      (v) => {
        if (typeof v !== 'object' || v === null || Array.isArray(v)) return undefined
        const raw = v as Record<string, unknown>
        const tiers = Array.isArray(raw.tiers)
          ? raw.tiers.slice(0, TIER_ROWS_MAX).filter((r) => tierRowSchema.safeParse(r).success)
          : undefined
        return { ...raw, tiers }
      },
      tierRatingSchema,
    ).describe('Tier Rating badge (toolkit tier-rating): an ordered ladder of taxonomy terms, each a tier with a label, stars, optional art, a tint and a description. A home shows the first tier whose term it carries'),
    // Card tags (#1812): the shared render-boundary wrapper (slice, then drop
    // malformed rows) — the same object related-properties declares (#1819).
    cardTags: cardTagsSettingSchema,
    photosLimit: wpLenientNumberDefault(z.number().int().min(1).max(20), 7).describe('Max photos in the card slideshow'),
    // '3/4' is the portrait ratio the toolkit Skin offers (card.image_aspect);
    // added here rather than as a second setting so there is one aspect control.
    imageAspectRatio: z.enum(CARD_IMAGE_ASPECT_RATIOS).default('16/9').describe('Card image aspect ratio'),
    slideAnimation: z.enum(['fade', 'slide', 'scale']).default('fade').describe('Card photo slideshow transition'),
    // Grid-left/right taxonomy badges (WP: grid_left/right_taxonomy + *_limit). The
    // left slot is gated by showLocation, the right by showType (back-compat).
    gridLeftTaxonomy: z.enum(['type', 'city', 'location', 'group']).default('city').describe('Taxonomy shown on the card left (WP: grid_left_taxonomy)'),
    gridRightTaxonomy: z.enum(['type', 'city', 'location', 'group']).default('type').describe('Taxonomy shown on the card right (WP: grid_right_taxonomy)'),
    gridLeftLimit: wpLenientNumberDefault(z.number().int().min(1).max(10), 5).describe('Max terms shown on the card left (WP: grid_left_limit)'),
    gridRightLimit: wpLenientNumberDefault(z.number().int().min(1).max(10), 5).describe('Max terms shown on the card right (WP: grid_right_limit)'),
    // Card subtitle (WP: subtitle_source / alternate_subtitle_source). Falls back to the
    // alternate when the primary source is empty; 'none' hides the subtitle.
    gridLeftIcon: z.string().default('').describe('Icomoon icon class for the left card badge; empty = default location pin (WP: grid_left_icon)'),
    gridRightIcon: z.string().default('').describe('Icomoon icon class for the right card badge; empty = default tag icon (WP: grid_right_icon)'),
    subtitleSource: z.enum(['none', 'post_title', 'post_excerpt', 'name', 'city', 'state', 'country']).default('none').describe('Card subtitle source (WP: subtitle_source)'),
    alternateSubtitleSource: z.enum(['none', 'post_title', 'post_excerpt', 'name', 'city', 'state', 'country']).default('none').describe('Fallback subtitle source when the primary is empty (WP: alternate_subtitle_source)'),

    // ── HomeRunner Skin card template, "Classic" preset (#872) ──────────────
    // #902: THE SHARED CARD-SKIN LEAVES. Spread, not declared — the same
    // `cardSkinFields` object is spread into related-properties' config, which
    // renders the same card component through common/property-card/
    // TemplateExplorer. The full contract (neutral defaults emit no CSS, why
    // sizing leaves carry no default, what adding a leaf costs) is documented
    // on the definition in common/property-card/card-skin-fields.ts.
    ...cardSkinFields,

    // Load More button (#872 close-out): see explorerLoadMoreFields above.
    ...explorerLoadMoreFields,
  }).default({
    openInNewTab: false,
    showLocation: true,
    showType: true,
    showFeatures: true,
    showRating: false,
    showGroups: true,
    photosLimit: 7,
    imageAspectRatio: '16/9',
    slideAnimation: 'fade',
    gridLeftTaxonomy: 'city',
    gridRightTaxonomy: 'type',
    gridLeftLimit: 5,
    gridRightLimit: 5,
    subtitleSource: 'none',
    alternateSubtitleSource: 'none',
  }).describe('Property card display settings'),
  // #1432: operator-saved Card Template presets for THIS explorer instance
  // (see cardSkinPresetSchema above). Dashboard-only data; the runtime ignores it.
  cardSkinPresets: cardSkinPresetsSchema.describe('Saved Card Template presets (name + the non-neutral skin knobs captured at save time). Applied by the dashboard as a form fill; never read at render time'),
  priceDisplay: z.object({
    withDates: z.enum(['lowest', 'base', 'average', 'total', 'per_night', 'none']).default('average').describe('Price shown after a date search (WP: with_dates_price = lowest/base/average/none; total & per_night are SSR extras)'),
    noDates: z.enum(['base', 'lowest', 'average', 'none']).default('lowest').describe('Price shown before a date search (WP: no_dates_price = lowest/base/average/none)'),
    // Toolkit: lowest-rate-availability "No dates available" label (#1064 Wave 4).
    // Both leaves are optional with NO schema default on purpose: priceDisplay is
    // in explorerGlobalSchema's pick, and a materialized default would be written
    // by the next widget-level save and clobber a feed-global value through
    // getFinalSettings (the sizing-leaf rule, see card-skin-schema.test.tsx).
    // Defaults apply at the point of use (common/property-card/no-dates-label.ts):
    // absent = shown, absent text = "No dates available".
    showNoDatesLabel: lenientBool().describe('Show a label instead of a price when the property has no bookable nights. The lowest and average nightly rates exclude unavailable and booked dates, so a listing nobody can book gets the label rather than its base rate. Applies to the Lowest Rate, Average and dated price modes; Base Rate always shows the base rate. OPT-OUT: absent = shown, set false to fall back to the base rate. Toolkit: lowest-rate-availability'),
    noDatesLabel: z.string().trim().optional().describe('Text of that label; absent = "No dates available"'),
  }).default({ withDates: 'average', noDates: 'lowest' }).describe('Card price-display mode, independent before vs. after a date/availability search'),
  sort: z.object({
    // z.preprocess keeps legacy free-text values from breaking parse: anything
    // that isn't a known key (the field used to be a plain text input, #557)
    // collapses to 'featured'. Effects-wrapped enum still renders as a select
    // in the dashboard's zod-form.
    default: z.preprocess(
      (v) => (typeof v === 'string' && (sortOptionKeys as readonly string[]).includes(v) ? v : 'featured'),
      z.enum(sortOptionKeys),
    ).describe('Default sort option offered to visitors; falls back to the first enabled sort option when not in the list (#557). WP: orderby_default'),
    options: z.array(z.enum(sortOptionKeys)).optional().describe('Sort options offered, in order (WP: orderby_options). Empty = all in default order.'),
  }).default({ default: 'featured' }).describe('Sort settings'),
  filter: phpObject(explorerFilterSchema.default({})),
  search: z.object({
    redirectOnSubmit: z.boolean().default(false).describe('When enabled, clicking Search navigates to the listings page (from the feed\'s reverseProxy.routes.listings) with the current filters as URL params, instead of filtering in place. Lets the filter bar be used as a standalone search widget outside the listings page.'),
    // #934: the toolkit module's hero/standalone variant (refine="0"). Hero is a
    // LAYOUT: the bar is the whole widget — results grid, map, and the results
    // header (count/sort/map toggle) are hidden by construction, and submit
    // always navigates (redirect-on-submit is implied, so the existing
    // redirect-gated behaviors — auto-advance chains, CTA count — turn on
    // without a second switch).
    heroBar: z.boolean().default(false).describe('Render as a standalone hero search bar (the toolkit module\'s homepage variant): only the bar renders — no results, map, or results header — and Search always navigates to the listings page. Grid/map settings are ignored while enabled.'),
    heroSticky: z.boolean().default(true).describe('Hero bar only: on scroll past the bar, detach to a compressed viewport-centered pill that expands back to the full bar on click (desktop only, matches the toolkit module\'s sticky behavior).'),
    // Host sites commonly run their own fixed or reveal-on-scroll-up header,
    // which the pinned pill (top:16px) and expanded panel (top:0) would sit
    // underneath. 0 keeps exact module parity.
    heroStickyTopPx: z.coerce.number().int().min(0).max(400).default(0).describe('Extra top offset (px) for a pinned search bar — read by BOTH sticky modes (the hero pill/panel and the refine sticky bar under stickyFilter, #1092) so the pinned bar clears a fixed or scroll-revealed site header. 0 = no extra offset, the toolkit base position (the refine bar keeps its 12px inset, the hero pill its 16px).'),
  }).default({ redirectOnSubmit: false }).describe('Search submit behavior'),
  visitorFilterDefaults: phpObject(
    z.object({
      cities: z.string().optional().describe('Comma-separated city IDs'),
      types: z.string().optional().describe('Comma-separated property type IDs'),
      amenities: z.string().optional().describe('Comma-separated amenity IDs'),
      locations: z.string().optional().describe('Comma-separated location IDs'),
      groups: z.string().optional().describe('Comma-separated group IDs'),
      tags: z.string().optional().describe('Comma-separated tag IDs'),
      bedrooms: z.coerce.number().int().optional().describe('Default minimum bedrooms'),
      bathrooms: z.coerce.number().int().optional().describe('Default minimum bathrooms'),
      price_min: z.coerce.number().optional().describe('Default minimum price'),
      price_max: z.coerce.number().optional().describe('Default maximum price'),
      bedrooms_eq: z.coerce.number().int().optional().describe('Default exact bedrooms (overrides min bedrooms)'),
      featured: z.coerce.boolean().optional().describe('Show featured properties only'),
      exclude_property: z.string().optional().describe('Property slug to exclude from results'),
      exclude: z.string().optional().describe('Comma-separated Central property IDs to exclude from results'),
      ctid: z.string().optional().describe('Comma-separated Central property IDs to restrict results to'),
    }).default({})
  ).describe('Default visitor filter values — applied on load, can be overridden by URL params'),
  // ── Searchbar Pro port (#853). Variant default is 'pro' (Eli, 8/4: "no one
  // is live with widget homerunner now" — recorded on #853): stored widgets
  // without the key parse straight to the new look (zero-write migration),
  // 'legacy' stays selectable as the opt-out. Every nested phpObject carries
  // .default({}) and every leaf a toolkit-derived default, so a fresh widget
  // stores only {} and parsing fills the rest.
  searchbar: phpObject(z.object({
    variant: lenientEnumDefault(['legacy', 'pro'], 'pro')
      .describe("Search bar look: 'pro' is the Searchbar Pro port (default for all widgets per #853 decision 2), 'legacy' is the original filter bar"),
    placeholder: lenientStringDefault('Where are you going?')
      .describe('Placeholder text in the Where field. Toolkit: default_placeholder'),
    showResultCount: lenientBoolDefault(false)
      .describe('Show a live matching-property count on the search button, from the fetched total. Toolkit: enable_cta_count'),
    // #1844: NO literal default. Unset = the theme accent (lightModeColors /
    // darkModeColors .accent): buildSearchbarCss then emits var(--hfse-accent),
    // which the palette hook already dual-emits for light, dark and the auto
    // first paint, so the two accent systems cannot disagree. The toolkit's own
    // default is #ff385c (Airbnb red) — a fresh explorer on a blue-themed site
    // painted a red search button (Foothills GAW, feed 416).
    accentColor: lenientHex()
      .describe('Search bar accent color (submit button, focus rings, active states). Empty = the theme accent (Light/Dark Mode Colors → Accent), so the bar follows the rest of the component. Hover shade and focus ring are derived (RGB ×0.88, 25% alpha). Toolkit: accent_color (the toolkit defaults to #ff385c; the component inherits the theme instead, #1844)'),
    shape: lenientEnumDefault(['pill', 'rounded', 'square'], 'pill')
      .describe('Master corner shape for the bar, buttons, chips, and popovers: pill (999px), rounded (12px), square (0). Toolkit: shape'),
    locationsOrder: lenientEnumDefault(['custom', 'alpha'], 'custom')
      .describe("Order of the Where field's destination suggestions: 'custom' keeps the curated order set under Filter Options (or the feed's own order when nothing is curated), 'alpha' sorts them A-Z. Toolkit: locations_order"),
    // #922 port of toolkit #461: ONE promoted slot between Who and the
    // Filters field, as an enum rather than per-field booleans — it caps the
    // bar at a single promoted control so the layout can never accumulate six
    // steppers; future candidates (bathrooms, price) become new choices, not
    // a redesign. The promoted control shares the applied bedrooms filter
    // with the filter-pane stepper, so the URL contract and the pane's badge
    // count are unchanged.
    barPromotedFilter: lenientEnumDefault(['none', 'bedrooms'], 'none')
      .describe('Promote one filter into the search bar as its own field between Who and the Filters field. Desktop bar only; the mobile sheet keeps its stage flow. Toolkit: bar_promoted_filter'),
    // #1700 port of toolkit #816 (bar_layout, 3.278.0/3.279.0): `labelled` is
    // the prior bar and the default; `compact` drops the Where / When / Who /
    // What label row, swaps the three idle texts for the compact placeholders
    // below, and turns the Filters field into an icon-only button with the
    // clear control as an accent badge. Hero and refine bars share the desktop
    // bar so both follow it; the phone sheet is unchanged. Defaults are the
    // toolkit's schema defaults (Shortcode::get_settings_schema).
    barLayout: lenientEnumDefault(['labelled', 'compact'], 'labelled')
      .describe('Search bar layout: labelled (default) shows the Where / When / Who / What labels; compact drops the label row, uses the compact placeholders below, and turns the Filters field into an icon-only button with the clear control as an accent badge while a filter is applied. Applies to the hero and refine bars; the phone search flow is unchanged. Toolkit: bar_layout'),
    compactPlaceholderWhere: lenientTrimmedStringDefault('Destination')
      .describe('Compact layout only: placeholder in the Where field. Replaces the Where placeholder above in compact; blank uses Destination. Toolkit: compact_placeholder_where'),
    compactPlaceholderWhen: lenientTrimmedStringDefault('Dates')
      .describe('Compact layout only: text in the When field when no dates are picked. Blank uses Dates. Toolkit: compact_placeholder_when'),
    compactPlaceholderWho: lenientTrimmedStringDefault('Guests')
      .describe('Compact layout only: text in the Who field when the guest count is untouched. Blank uses Guests. Toolkit: compact_placeholder_who'),
    // #923 port of toolkit PR #469 + 985682c: the desktop bar's chained
    // popovers. Default ON to match the module (its schema comment: existing
    // installs keep the shipped destination→dates advance and gain the
    // dates→guests step; the setting is the opt-out).
    autoAdvance: lenientBoolDefault(true)
      .describe('Chain the search-bar fields: picking a destination opens the date picker, a completed date range advances to guests, and guests Apply hands off to the promoted filter or the Filters popup. Desktop bar only — the mobile sheet keeps its stage flow. Toolkit: auto_advance'),
    // ── Bar sizing (#870, Eli QA 8/5 item 7). NET-NEW, not a port: the module
    // has NO width or font-size setting anywhere in its schema
    // (Shortcode::get_settings_schema) — it hardcodes max-width 850px and the
    // --hrtk-sbp-label-size / --hrtk-sbp-input-size tokens at 11px/15px. The
    // defaults below are those hardcoded module values, so a widget that never
    // touches these keys renders at exact module parity. The two type scales
    // are INDEPENDENT (the module never derives one from the other), which is
    // why this is two keys and not one. Emitted as --hfse-sbp-max-width /
    // --hfse-sbp-label-size / --hfse-sbp-input-size by buildSearchbarCss; the
    // consuming rules live in explorer.css with matching fallbacks.
    maxWidthPx: lenientIntDefault(320, 1600, 850)
      .describe('Maximum width of the Pro search bar, in px. The bar stays centered and shrinks to fit narrower containers. It is replaced by the compact trigger below the mobile breakpoint, and also whenever its container (or this width) is too narrow for its fields — about 600px with all four fields, so a value below that shows the trigger on desktop too. Module hardcodes 850'),
    labelSizePx: lenientIntDefault(8, 24, 11)
      .describe('Font size of the uppercase field labels (WHERE / WHEN / WHO) inside the search bar, in px. Independent of the value size. Module hardcodes 11'),
    inputSizePx: lenientIntDefault(10, 32, 15)
      .describe('Font size of the field values and the Where input inside the search bar, in px. Independent of the label size. Module hardcodes 15'),
    scrim: phpObject(z.object({
      style: lenientEnumDefault(['white', 'dark', 'none'], 'white')
        .describe('Backdrop gradient behind the sticky search bar while scrolling results. Toolkit: scrim_style'),
      blur: lenientBoolDefault(false).describe('Frost the scrim with a backdrop blur. Toolkit: scrim_blur'),
      heightPx: lenientIntDefault(0, 400, 60).describe('How far the scrim fade extends below the sticky bar, in px. Toolkit: scrim_height'),
    }).default({})).describe('Sticky-bar scrim (only renders when the sticky filter bar is enabled)'),
    nameFinder: phpObject(z.object({
      enabled: lenientBoolDefault(false)
        .describe('Offer a "By name" property lookup in the Where field; selecting a match navigates straight to that property. Requires the Central property-names endpoint (central#2885) — hidden gracefully until it is available. Toolkit: enable_name_finder'),
      showAllBeforeTyping: lenientBoolDefault(false)
        .describe('Show the full property-name list before the visitor types (small portfolios only). Toolkit: name_finder_show_all'),
      serverLookupThreshold: lenientIntDefault(50, 5000, 500)
        .describe('Portfolio size at/below which the full name list is fetched once and matched client-side; above it, each keystroke queries the server. Toolkit: name_finder_threshold'),
    }).default({})).describe('Find-by-name property lookup'),
    autocomplete: phpObject(z.object({
      minQueryLength: lenientIntDefault(1, 10, 2).describe('Characters typed before destination suggestions appear. Toolkit: min_query_length'),
      debounceMs: lenientIntDefault(0, 2000, 250).describe('Debounce after the last keystroke before filtering suggestions. Toolkit: debounce_ms'),
      maxResults: lenientIntDefault(1, 50, 8).describe('Maximum destination suggestions shown.'),
    }).default({})).describe('Where-field destination autocomplete behavior'),
    // #925: mobile-sheet-only knobs. NET-NEW group — the module has no
    // input-hide equivalent (its show_where removes the whole field), so
    // these keys are widget-native, not toolkit-mapped.
    mobileSheet: phpObject(z.object({
      // #935 item 3 widened the old boolean to a tri-state (Eli r6: default
      // flips to show-search when the destination list outgrows the
      // threshold). Stored booleans — and the settings sheet's "1"/"" forms —
      // keep their old meaning as the forced states.
      showSearchInput: z.preprocess((v) => (
        v === true || v === '1' || v === 1 || v === 'true' ? 'show'
          : v === false || v === '0' || v === 0 || v === 'false' || v === '' ? 'hide'
            : typeof v === 'string' && ['auto', 'show', 'hide'].includes(v) ? v
              : undefined
      ), z.enum(['auto', 'show', 'hide']).default('auto'))
        .describe('Destination search input inside the mobile sheet Where stage: auto shows it only when there are more destinations than the threshold below; show/hide force it either way. Legacy true/false values read as show/hide. The desktop bar is unaffected'),
      searchThreshold: lenientIntDefault(1, 500, 6)
        .describe('Auto mode: show the search input when the destination list is longer than this many entries (#935 item 3)'),
      autoAdvance: lenientBoolDefault(true)
        .describe('Phone sheet: picking a destination, dates or guests moves to the next stage on its own. Off keeps the visitor on the stage until they tap Next or another stage. Applies on top of the desktop Auto-advance setting. Toolkit: mobile_auto_advance (#1589)'),
    }).default({})).describe('Mobile search sheet'),
    // ── Phrase settings (#935 item 1). The module hardcodes nearly all of
    // these as __() strings with no admin path (only default_placeholder and
    // default_cta carry copy in its schema), so this group is widget-native
    // plumbing, not a toolkit mapping. Defaults are the MODULE's shipped
    // strings — which is itself the r6 change: the WHEN field now idles
    // "Add dates" (module parity) instead of the old widget wording
    // "Any time".
    labels: phpObject(z.object({
      whereLabel: lenientStringDefault('Where').describe('Field/stage name for the Where field'),
      whenLabel: lenientStringDefault('When').describe('Field/stage name for the When (dates) field'),
      whoLabel: lenientStringDefault('Who').describe('Field/stage name for the Who (guests) field'),
      filtersLabel: lenientStringDefault('Filters').describe('Field/stage name for the Filters field'),
      whereIdle: lenientStringDefault('Anywhere').describe('Where field idle text before a destination is chosen. Module: "Anywhere"'),
      whenIdle: lenientStringDefault('Add dates').describe('When field idle text before dates are chosen. Module default "Add dates" (#935 r6: replaces the widget\'s old "Any time")'),
      whoIdle: lenientStringDefault('Add guests').describe('Who field idle text before guests are chosen. Module: "Add guests"'),
      addCheckout: lenientStringDefault('Add checkout').describe('Right half of the When label while only check-in is picked ("Jul 15 – Add checkout"). Module: DT.addCheckout'),
      startPrompt: lenientStringDefault('Start your search').describe('Mobile trigger pill headline when nothing is filled in yet'),
      clearAll: lenientStringDefault('Clear all').describe('Mobile sheet footer reset button'),
      next: lenientStringDefault('Next').describe('Mobile sheet footer manual stage-advance button'),
      searchDestinations: lenientStringDefault('Search destinations').describe('Mobile sheet Where stage: search input placeholder'),
      suggestedDestinations: lenientStringDefault('Suggested destinations').describe('Mobile sheet Where stage: header above the destination list'),
    }).default({})).describe('Search bar phrases (#935): the field labels and instruction texts the pro bar renders, editable per widget'),
    chips: phpObject(z.object({
      enabled: lenientBoolDefault(false).describe('Show the quick-filter chip row under the search bar. Toolkit: filter_chips_enabled'),
      showOnMobile: lenientBoolDefault(true)
        .describe('Also show the chip row on phones. On by default (Eli, 8/6): a chip row the admin configured should not silently vanish on phones. Deliberate divergence from the toolkit module, which hides chips below 768px (toolkit #284) because its mobile search sheet owns filtering there — turn this off per feed to match the module.'),
      items: lenientChipItems.describe('Quick-filter chips (max 20). Single-select: activating one clears the others. Toolkit: filter_chips, resolved to Central IDs'),
    }).default({})).describe('Quick-filter chip row'),
    // Flexible dates (#870 slice 6) — the toolkit's FlexibleSearch settings
    // (includes/FlexibleSearch/Settings.php:29-33), which live in a separate
    // module there and fold into the search bar here because the SBP bridge is
    // the only surface that renders them.
    flexDates: phpObject(z.object({
      enabled: lenientBoolDefault(false)
        .describe('Offer an "I\'m flexible" mode in the date picker: pick a stay length and a month instead of exact dates. Toolkit: FlexibleSearch enabled'),
      stayLengths: lenientStayLengths
        .describe('Which stay-length chips the flexible panel offers. Toolkit: stay_lengths'),
      monthsAhead: lenientIntDefault(6, 24, 12)
        .describe('How many months the flexible month strip lists after Anytime. Toolkit: months_ahead'),
      // Zero-results rescue (#1265): the suggestion window around an exact
      // dated search that returned nothing, in whole weeks. Same bounds as the
      // module (Settings::normalize_weeks 0..8 / 1..12); more than 8 windows in
      // total are sampled evenly down to the verification cap.
      // Clamping, not degrading: the module's normalize_weeks pins an
      // out-of-range integer to the nearest bound (Settings.php:137-146).
      windowBeforeWeeks: lenientIntClampDefault(0, 8, 2)
        .describe('Zero-results rescue: how many weeks BEFORE the searched dates to offer as verified alternative windows (0-8, clamped). Toolkit: window_before_weeks'),
      windowAfterWeeks: lenientIntClampDefault(1, 12, 4)
        .describe('Zero-results rescue: how many weeks AFTER the searched dates to offer as verified alternative windows (1-12, clamped). Toolkit: window_after_weeks'),
    }).default({})).describe('Flexible dates'),
    // Date-window gates (#1338 min-stay marking, #1339 advance-notice /
    // booking-window blocking). Unlike flexDates above, these three toolkit
    // values live OUTSIDE the SearchbarPro module's own settings-export
    // schema (confirmed absent from SearchbarPro::get_settings_schema()) —
    // homelocal_explorer_min_stay and homelocal_max_calendar_days are plain
    // homerunner-plugin options, and notice hours is the separate Advance
    // Notice Blocking toolkit module's own admin setting. So this group is
    // widget-native, not toolkit-import-mapped, and settings-parity-ledger
    // (which only tracks searchbar-pro/map-controls/map-viewport-fit) has
    // nothing to reconcile it against.
    //
    // Defaults mirror a FRESH, untouched toolkit install exactly, so turning
    // this group on is opt-in per feed rather than a silent behavior change:
    //   minStay: 1            — homelocal_explorer_min_stay's own default
    //                           (ExplorerSettings.php), constrains nothing
    //   advanceNoticeHours: 0 — the Advance Notice Blocking module ships
    //                           DISABLED (absent from Modules\Registry::
    //                           LEGACY_DEFAULTS), so 0/off is the fresh-site
    //                           behavior even though the module's own settings
    //                           FORM pre-fills 168 once an admin turns it on
    //   maxBookingDays: 365   — homelocal_get_public_max_calendar_days(0)'s
    //                           own default (homelocal_get_default_max_
    //                           calendar_days()); unlike notice hours this one
    //                           is a plain always-active plugin setting, so
    //                           reproducing its default DOES start blocking
    //                           past day 365 out of the box — that is the
    //                           toolkit's own out-of-the-box behavior, not a
    //                           new restriction invented here
    //
    // The Explorer Dynamic Min Stay per-date AGGREGATE (the toolkit module
    // that computes a per-date minimum via WP-cron over feed inventory) DOES
    // have a Central equivalent now: `feed/explorer-meta`'s `minStayByDate`
    // (central#3208), read by DateFilter's effectiveMinStay (#1071). minStay
    // below is the static site-wide FLOOR: the live per-date aggregate can
    // raise it for individual dates but never lowers it, and a date with no
    // aggregate (older Central, dev mode, outside the horizon) uses the
    // static number alone — same as a fresh toolkit install with that module
    // off. The toolkit's min-stay zero-results state is not ported (#1071).
    dateWindow: phpObject(z.object({
      minStay: lenientIntDefault(1, 100, 1)
        .describe('Minimum nights required for a stay searched in the date picker. 1 = no minimum. The feed\'s live per-date minimum can raise it on individual dates but never lowers it. Nights inside the window are blocked once a check-in is picked, and the When badge reads "Select check-out - N-night minimum".'),
      advanceNoticeHours: lenientIntDefault(0, 24 * 365, 0)
        .describe('Hours of advance notice required before the earliest bookable check-in. 0 = off (default; matches the toolkit Advance Notice Blocking module shipping disabled).'),
      maxBookingDays: lenientIntDefault(1, 3650, 365)
        .describe('How many days ahead a visitor may search. Default 365 matches the toolkit default (homelocal_max_calendar_days).'),
    }).default({})).describe('Date-picker gating (#1338/#1339): minimum stay, advance notice, and the booking-window ceiling for the SBP explorer date picker'),
    what: phpObject(z.object({
      byNameTab: lenientBoolDefault(true)
        .describe('Offer a "By name" tab in the What popover. On by default; the tab still hides itself until the Central property-names endpoint (central#2885) answers, so turning it off is an opt-out, not a prerequisite. Toolkit: enable_name_finder'),
      aiConcierge: lenientBoolDefault(false)
        .describe('AI Concierge is enabled for this feed. Gates the "Describe" tab in the What popover. Feeds cannot run the concierge yet, so the tab ships hidden. Toolkit: has_ai_search'),
      describePlaceholder: lenientStringDefault('Ask for specific things like a bright, modern kitchen and a yard.')
        .describe('Placeholder inside the Describe box. Toolkit: describe_placeholder'),
      suggestedDescriptions: lenientStringLines(8, [
        'Spacious place with bunk beds and board games',
        'A pool, outdoor dining area, hammocks, and a fire pit',
        'Mountain retreat with a hot tub and fireplace',
      ]).describe('Tappable example descriptions under the Describe box (max 8). Toolkit: suggested_descriptions'),
    }).default({})).describe('What popover tabs (Filter / Describe / By name)'),
    nearby: phpObject(z.object({
      enabled: lenientBoolDefault(false)
        .describe('Offer a "Nearby" row in the Where field that resolves the visitor location to the closest destination within 300 km, then searches it — the toolkit behaviour, which picks a DESTINATION rather than running a radius search. Off by default, matching the toolkit. The row stays hidden until a nearby index has been built under Filter Options, so enabling this without one shows nothing rather than a broken row. Toolkit: enable_nearby'),
      centroids: nearbyCentroidsField(),
    }).default({})).describe('Nearby destination search (#861, toolkit: Searchbar Pro enable_nearby)'),
  }).default({})).describe('Search bar appearance and behavior (Toolkit: Searchbar Pro port, #853)'),
  // ── Mobile results sheet (#856) — toolkit explorer-mobile-sheet v2 port.
  // A phone LAYOUT variant, not a search setting: the map becomes a
  // full-bleed backdrop, the results grid lives in a draggable bottom sheet
  // and a floating pill flips Map ↔ List. Applies client-side after
  // hydration only (no SSR/desktop shift). Distinct from `searchbar.mobileSheet`,
  // which is the Searchbar Pro SEARCH sheet (Where/When/Who stages, #925).
  // Off by default like the toolkit module. Needs `showMap`; hero bars
  // (search.heroBar) ignore it since they render no results.
  mobileResults: phpObject(z.object({
    enabled: lenientBoolDefault(false)
      .describe('Map-first phone layout: full-bleed map with the results in a draggable bottom sheet and a Map/List toggle. Off = the stacked mobile layout. TAKES OVER THE PHONE VIEWPORT: while it is on, the explorer owns the whole screen on narrow devices — the host page\'s own scrolling is locked and anything above or below the widget is unreachable until the visitor leaves the page. Turn it on only where the explorer IS the page (a dedicated search/listings page), not for an explorer embedded partway down a longer page. Desktop and tablets above the breakpoint are unaffected. Needs "Show map". Toolkit: Explorer Mobile Sheet module (default off)'),
    navOffsetPx: lenientIntDefault(0, 400, 0)
      .describe('Push the sticky search CTA down by this many px so it sits below the host theme\'s own mobile navigation bar; 0 = flush to the top. Toolkit: below_theme_nav + nav_offset (default 80 when that mode is on)'),
    initialSnap: lenientEnumDefault(['half', 'peek'], 'half')
      .describe('Where the results sheet rests on load: half = the sheet covers the lower half of the map (toolkit default); peek = map-first, only the handle and the count show'),
    showToggle: lenientBoolDefault(true)
      .describe('Show the floating Map / List pill that jumps the sheet between the full list and the map. The sheet handle still drags either way'),
    breakpointPx: lenientIntDefault(320, 932, 768)
      .describe('Viewports at or under this width (px) get the sheet layout. Default 768 = the toolkit module\'s hardcoded breakpoint (innerWidth <= 768), also the explorer\'s chip-row cutover. Capped at 932 so the Searchbar Pro desktop bar (933px+) never lands in the sheet\'s top bar'),
  }).default({})).describe('Mobile results layout: map-first bottom sheet (toolkit explorer-mobile-sheet port, #856)'),

  map: z.object({
    provider: z.enum(['leaflet', 'google']).default('leaflet').describe('Map provider'),
    apiKey: z.string().optional().describe('Map API key (for Google Maps)'),
    searchOnMove: wpLenientBoolDefault(true).describe('Filter properties by map bounds on move (feature gate for the search-as-move checkbox)'),
    zoomMin: wpLenientNumberDefault(z.number().int(), 0).describe('Minimum zoom level'),
    zoomMax: wpLenientNumberDefault(z.number().int(), 18).describe('Maximum zoom level'),
    placement: z.enum(['left', 'right']).default('right').describe('Map placement'),
    showPrice: wpLenientBoolDefault(true).describe('Show price on marker badges'),
    // Google-only (WP: map_type / map_styles / map_hide_local_business).
    mapType: z.enum(['roadmap', 'satellite', 'hybrid', 'terrain']).default('roadmap').describe('Google base map type (WP: map_type)'),
    mapStyles: z.string().optional().describe('Google map styles as a JSON array string (WP: map_styles)'),
    hideLocalBusiness: wpLenientBoolDefault(false).describe('Hide local-business POIs on Google maps (WP: map_hide_local_business)'),
    // Map appearance (#567). Colors default empty = theme styling; the
    // infowindow icons are image URLs defaulting to the dashboard-hosted
    // copies of the WP plugin's bundled SVGs.
    controlBg: z.string().default('').describe('Map control button background color; empty = theme card background (WP: map_control_bg)'),
    controlColor: z.string().default('').describe('Map control button icon color; empty = theme foreground (WP: map_control_color)'),
    infowindowLocationsIcon: z.string().default('').describe('Icon image URL next to the location in the map popup; empty = bundled default from the widget asset base, "none" hides it (WP: map_infowindow_locations_icon)'),
    infowindowTypesIcon: z.string().default('').describe('Icon image URL next to the property type in the map popup; empty = bundled default from the widget asset base, "none" hides it (WP: map_infowindow_types_icon)'),
    // ── Toolkit map-module port (#852). Every key below is OPTIONAL with NO
    // schema default: absent must stay absent in stored settings, so a
    // widget-level save can never materialize a default that clobbers the
    // feed-global value through the getFinalSettings merge (the same reason
    // apiKey has no default). Behavior fallbacks live in the consumers.
    tileProvider: lenientEnum(['osm', 'mapbox']).describe('Leaflet tile provider; absent = OpenStreetMap. Toolkit port: Mapbox tiles option'),
    mapboxPublicToken: lenientPattern(/^pk\.[A-Za-z0-9._-]{8,200}$/)
      .describe('Mapbox PUBLIC access token (pk.… — a secret sk. token is rejected). Public by nature — it rides every tile URL; restrict it to your domains in the Mapbox dashboard. Configured at the feed level (like the Google Maps key). Invalid values are treated as unset (OSM fallback)'),
    mapboxStyle: lenientPattern(/^[\w-]+\/[\w.-]+$/)
      .describe('Mapbox style as owner/style-id (e.g. mapbox/streets-v12); absent or invalid = mapbox/streets-v12'),
    viewportFit: phpObject(z.object({
      enabled: lenientBool().describe('Fit the sticky desktop map to the visible viewport. Toolkit: map-viewport-fit module'),
      topOffsetPx: lenientInt(0, 1000).describe('Distance from the window top the map sticks at (set to a fixed-nav height). Toolkit: hrtk_mvf_top_offset'),
      bottomGapPx: lenientInt(0, 1000).describe('Gap between the map bottom and the viewport bottom. Toolkit: hrtk_mvf_bottom_gap'),
    }).optional()).describe('Viewport-fit sizing for the sticky desktop map (960px+ only)'),
    splitPct: phpObject(z.object({
      md: lenientInt(20, 70).describe('Map width % at 960-1199px; empty = default 45%. Toolkit: hrtk_mapctl_split_md'),
      lg: lenientInt(20, 70).describe('Map width % at 1200-1599px; empty = default 45%. Toolkit: hrtk_mapctl_split_lg'),
      xl: lenientInt(20, 70).describe('Map width % at 1600px+; empty = default 45%. Toolkit: hrtk_mapctl_split_xl'),
    }).optional()).describe('Map/grid width split per desktop breakpoint. Toolkit: map-controls splits'),
    searchAreaButton: lenientBool().describe('Show a one-shot "Search this area" pill after the visitor moves the map while search-as-move is off; clicking it turns search-as-move on from the current bounds. Toolkit: hrtk_mapctl_search_area'),
    showFullscreen: lenientBool().describe('Show the fullscreen map control; absent = shown (current behavior). Toolkit: hrtk_mapctl_fullscreen'),
    activateSearchOnFirstGesture: lenientBool().describe('Arm search-as-move to turn itself on at the visitor\'s FIRST genuine pan/pinch/zoom gesture (never during the initial programmatic map framing, which would narrow the unfiltered total). Toolkit: search-as-move-default module. Superseded by searchOnMoveDefault when that is set'),
    searchOnMoveDefault: lenientEnum(['off', 'on', 'gesture']).describe('Initial state of the search-as-move checkbox (SBP-10, #929): absent/off = unchecked, the visitor opts in (module fresh-site behavior); on = checked from load, bounds attach on the first real move; gesture = arm to auto-check on the FIRST genuine pan/pinch/zoom (module search-as-move-default). Overrides activateSearchOnFirstGesture when set'),
    zoomDamping: lenientBool().describe('Damp inertial wheel/trackpad zoom on the Leaflet map — higher wheel debounce, more scroll per zoom level, and a momentum dead zone so a Magic Mouse / trackpad flick lands one zoom instead of several. OPT-OUT: absent = enabled, set false to disable. Leaflet only. Toolkit: map-polish zoom damping'),
    stacking: phpObject(z.object({
      enabled: lenientBool().describe('Master switch for marker clustering / co-located stacking on the explorer map (Leaflet and Google providers); absent = off. Toolkit: map-stacking module (#854, #1203)'),
      clusterMode: lenientBool().describe('Density clustering (count bubbles that zoom in / spiderfy at max zoom); absent = ON when stacking is enabled (toolkit cluster-primary default). Set false for the stacked-badge mode: only pins sharing an exact coordinate collapse into one price-range badge that fans out on click'),
      fadeOpacityPct: lenientInt(0, 100).describe('Opacity (%) the other markers fade to while a stack is fanned out; absent = 40. Toolkit: fade_opacity'),
      badgeBg: lenientHex().describe('Stacked badge background; absent = #1a1a2e. Toolkit: badge_bg_color'),
      badgeText: lenientHex().describe('Stacked badge text; absent = #ffffff. Toolkit: badge_text_color'),
      countBg: lenientHex().describe('Count bubble background; absent = #058ef9. Toolkit: count_bg_color'),
      countText: lenientHex().describe('Count bubble text (also the cluster-bubble count); absent = #ffffff. Toolkit: count_text_color'),
      centerDot: lenientHex().describe('Spiderfy center dot; absent = #1a1a2e. Toolkit: center_dot_color'),
      connector: lenientHex().describe('Spiderfy connector legs; absent = #1a1a2e. Toolkit: connector_color'),
      focus: lenientHex().describe('Focused stack/spider highlight; absent = #3b82f6. Toolkit: focus_color'),
      clusterColor1: lenientHex().describe('Cluster bubble tint, tier 1 (<10 pins) — tinted onto the bundled halo-ring marker via CSS mask; absent = #058ef9. Toolkit: cluster_color_1'),
      clusterColor2: lenientHex().describe('Cluster tier 2 (10–24); absent = #0b7fd4. Toolkit: cluster_color_2'),
      clusterColor3: lenientHex().describe('Cluster tier 3 (25–49); absent = #7a5cd6. Toolkit: cluster_color_3'),
      clusterColor4: lenientHex().describe('Cluster tier 4 (50–99); absent = #d1568a. Toolkit: cluster_color_4'),
      clusterColor5: lenientHex().describe('Cluster tier 5 (100+); absent = #e2603f. Toolkit: cluster_color_5'),
    }).optional()).describe('Marker clustering & co-located stacking on the explorer map, Leaflet and Google providers. Toolkit: map-stacking port (#854, #1203)'),
    allPins: phpObject(z.object({
      enabled: lenientBool().describe('Render EVERY home in the filtered result set on the explorer map (Leaflet and Google) — homes beyond the loaded page appear as lightweight dot pins with a compact click card; absent = off. Data source: the Central property-pins endpoint (central#3106) — until it ships, enabling this is a harmless no-op. Toolkit: map-all-pins module (#855, Google: #1313)'),
    }).optional()).describe('All-pins mode: the full filtered set on the map, not just the loaded page. Toolkit: map-all-pins port (#855)'),
  }).default({ provider: 'leaflet', searchOnMove: true, zoomMin: 0, zoomMax: 18, placement: 'right', showPrice: true, mapType: 'roadmap', hideLocalBusiness: false }).describe('Map settings'),
  features: z.object({
    aiSearch: z.boolean().default(false).describe('Enable AI-powered search'),
    relevanceSearch: z.boolean().default(false).describe('Enable relevance search'),
    favorites: z.boolean().default(false).describe('Enable favorites'),
    recommendations: z.boolean().default(false).describe('Show recommendations when no results'),
  }).default({ aiSearch: false, relevanceSearch: false, favorites: false, recommendations: false }).describe('Feature toggles'),

  lightModeColors: explorerColorSchema(explorerLightColors).default(explorerLightColors).describe('Colors in light mode'),

  darkModeColors: explorerColorSchema(explorerDarkColors).default(explorerDarkColors).describe('Colors in dark mode'),

  // ── Destination Filter port (#1072 D1, toolkit destination-filter module).
  // The tree is DERIVED IN THE DASHBOARD APP on an operator "build tree"
  // action and stored here; visitors never derive. See destinations/tree.ts
  // for the algorithm and for why homes/hygiene are not stored.
  // Lenient like searchbarChipRows: exploreWidgetConfig.parse runs at RENDER
  // time, so a damaged tree must degrade to "no tree" (the Where field falls
  // back to its normal taxonomy options), never throw a ZodError that bricks
  // the widget (#858, #1168). Malformed rows are dropped individually; a
  // non-array becomes undefined. The preprocess wrapper does not change the
  // materialized blank — ZodEffects unwraps to the optional array, which still
  // seeds as `undefined` (see destinationNodeSchema above).
  destinationTree: destinationTreeField()
    .describe('Derived destination tree, flat node rows. Written by the Destination Filter panel\'s build action, not hand-edited. Absent = no tree built yet, and the Where field falls back to its normal taxonomy options.'),

  // The widget reads enabled/popupTitle/showCounts/groupOrder. The prefix
  // inputs and the build stamp are operator-side: only the dashboard
  // Destination Filter panel (destination-tree-builder.tsx) reads them,
  // which is invisible to the dead-setting sentinel's AST walk — they are
  // declared in the consumption manifest with file+line citations. Removed
  // in ec082dbc while no consumer existed; restored in the same commit as
  // the panel, per the sentinel's "fix the setting, do not baseline" rule.
  destinations: phpObject(z.object({
    enabled: z.boolean().default(false)
      .describe('Replace the explorer Where control with the tabbed destination popup. Off until a tree is built. WP: the module being active'),
    // Length clamps (slice, not reject — a reject would throw at the visitor
    // parse): these strings ship in every widget payload, so give each a
    // sane byte bound instead of trusting the writer.
    popupTitle: z.preprocess(
      (v) => (typeof v === 'string' ? v.slice(0, 200) : v),
      lenientStringDefault('Find Your Destination'),
    ).describe('Heading shown at the top of the destination popup. WP: popup_title'),
    showCounts: z.boolean().default(true)
      .describe('Show the property count beside each destination. WP: show_counts'),
    // Per-feed display order of the popup groups (#1493 sub-item 1, toolkit
    // destination-filter's move-up/move-down groups control). Read by
    // tabs.ts's computeDestinationTabs; absent = the pre-#1493 hardcoded
    // sequence, byte-identical for every unconfigured feed.
    groupOrder: destinationGroupOrderField(),
    // Prefix inputs + build stamp: shared with destination-search via
    // destinations/schema.ts (blank prefix = default; see the module doc).
    ...destinationPrefixFields,
  }).default({})).describe('Tabbed destination popup over the Where field (Toolkit: destination-filter port, #1072)'),
  translations: explorerTranslationsSchema.default(explorerTranslationDefaults).describe('The translations of the widget'),
})

export type ExploreWidgetConfig = z.infer<typeof exploreWidgetConfig>
// Input-side type (defaults optional, pre-parse): what patch-producers like the
// toolkit importer emit — NEVER the output type, which would materialize
// defaults into required fields (#852).
export type ExploreWidgetConfigInput = z.input<typeof exploreWidgetConfig>

/**
 * Central (Laravel) converts empty strings to null on save
 * (ConvertEmptyStringsToNull), so stored settings routinely carry `null` where
 * the editor sent '' — e.g. filterOptions.cities, card.gridLeftIcon. The
 * schema's plain string leaves are not nullable, so a single such key made
 * `exploreWidgetConfig.parse` throw and bricked the widget at boot (verified
 * live on beta feed 8 during the #858 click-through).
 *
 * The implementation was promoted to widget-core in #1168 (it now also strips
 * '' leaves the leaf schema rejects — the pre-ConvertEmptyStringsToNull
 * legacy-blob artifact) and runs inside `parseWidgetConfig` at every widget's
 * parse sites. This alias keeps the explorer's historical one-arg call shape
 * (schema defaults to `exploreWidgetConfig`) for existing consumers and tests.
 */
export function stripNullSettingLeaves(value: unknown, schema: z.ZodTypeAny = exploreWidgetConfig): unknown {
  return coreStripNullSettingLeaves(value, schema)
}

// Feed-wide GLOBAL Explorer defaults (#436 §1). A curated subset of the per-widget
// config that can be set once on the feed (stored at feed.additional_info.explorer)
// and inherited by every Explorer widget instance unless that instance overrides it.
// Inheritance order at render: widget setting → feed global default → widget schema default.
// Every field is optional: absent means "no global default — fall back to the schema default".
export const explorerGlobalSchema = exploreWidgetConfig
  .pick({
    noItemsText: true,
    submitBtnText: true,
    fullPrice: true,
    fullPriceAfterTax: true,
    infiniteScroll: true,
    showLoadMore: true,
    priceStep: true,
    grid: true,
    card: true,
    priceDisplay: true,
    recommendations: true,
    map: true,
  })
  // deepPartial (not partial) so nested objects are recursively optional too: a global
  // value sets only the fields the admin touches, and the zod input/output types stay
  // aligned (no defaults forcing required-output) — required for the RHF resolver.
  .deepPartial()

export type ExplorerGlobalConfig = z.infer<typeof explorerGlobalSchema>
// Input-side counterpart for default-free patches (#852): z.infer would demand
// defaulted fields; patches written to feed.additional_info.explorer must not.
export type ExplorerGlobalConfigInput = z.input<typeof explorerGlobalSchema>
